import React, { useState } from "react";
import {
  Flag,
  Beer,
  Trophy,
  MapPin,
  PartyPopper,
  Clock,
  Heart,
  Navigation, // For the directions icon
  Copy, // For the copy button
  Check, // For the copy feedback
} from "lucide-react";

// --- MONDAY: GOLF SIM ---
export const GolfSimCard = ({ currentDay }) => {
  if (currentDay !== "2025-12-22") return null;

  return (
    <div className="mb-6 bg-stone-900 rounded-xl overflow-hidden shadow-md text-stone-300 border border-stone-700">
      <div className="bg-emerald-900/40 p-4 border-b border-emerald-900/50 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-500/20 p-2 rounded-full text-emerald-400">
            <Flag className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-emerald-100">GolfX Boys Night</h3>
            <p className="text-xs text-emerald-400/80">"Blue is home"</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-white">7:00 PM</div>
          <div className="text-xs text-stone-500">Franklin</div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <MapPin className="w-4 h-4 text-stone-500" />
          </div>
          <div className="text-sm">
            <span className="text-white font-medium">
              GolfX Suite (Upstairs)
            </span>
            <p className="text-xs text-stone-500 mt-0.5">
              BYOB • 1000 sq ft suite
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <Beer className="w-4 h-4 text-stone-500" />
          </div>
          <div className="text-sm">
            <span className="text-stone-300">
              After-party @{" "}
              <span className="text-white font-medium">Diggs Pub</span>
            </span>
            <p className="text-xs text-stone-500 mt-0.5">10:00 PM onwards</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- TUESDAY: ALUMNI GAME ---
export const AlumniGameCard = ({ currentDay }) => {
  if (currentDay !== "2025-12-23") return null;

  return (
    <div className="mb-4 bg-white border-l-4 border-amber-500 rounded-r-xl shadow-sm p-4">
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-3">
          <div className="bg-amber-50 p-2 rounded-full text-amber-600">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-stone-800">Alumni Basketball Game</h3>
            <p className="text-xs text-stone-500 font-medium">
              For Coach Chiappone
            </p>

            <div className="flex items-center gap-2 mt-2 text-sm text-stone-600">
              <Clock className="w-3 h-3 text-amber-500" />
              <span className="font-semibold">4:00 PM</span>
              <span className="text-stone-300">|</span>
              <MapPin className="w-3 h-3 text-stone-400" />
              <span>Bellingham HS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- TUESDAY: EVE EVE TRADITION ---
export const EveEveCard = ({ currentDay }) => {
  const [showDirections, setShowDirections] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  if (currentDay !== "2025-12-23") return null;

  const directionsText = `Directions to Arigna Irish Pub (from home):\n\n1. Take 140 North to Route 1 South.\n2. Merge onto I-495 North.\n3. Take Exit 43 (Franklin/Route 140).\n4. Follow 140 North (becomes Blackstone St).\n5. Turn onto South Main St (MA-126).\n6. Destination: Arigna Irish Pub (799 S Main St, Bellingham).`;

  const handleCopy = () => {
    navigator.clipboard.writeText(directionsText);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="mb-6 bg-gradient-to-br from-indigo-900 to-slate-900 rounded-xl shadow-lg text-indigo-100 overflow-hidden border border-indigo-500/30">
      <div className="p-4 border-b border-indigo-500/20 flex justify-between items-center bg-white/5">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-500/20 p-2 rounded-full text-indigo-300">
            <PartyPopper className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-white tracking-wide">
              Christmas Eve Eve
            </h3>
            <p className="text-[10px] uppercase tracking-widest text-indigo-300/80 font-bold">
              Tradition Since 2011
            </p>
          </div>
        </div>
        <Heart className="w-4 h-4 text-rose-400" />
      </div>

      <div className="p-4 space-y-4">
        {/* Phase 1 */}
        <div className="flex gap-4 relative">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-indigo-400"></div>
            <div className="w-0.5 h-full bg-indigo-500/20 my-1"></div>
          </div>
          <div>
            <span className="text-xs font-bold text-indigo-300 uppercase">
              Early Evening
            </span>
            <div className="text-sm font-medium text-white">
              Matt Blue's House
            </div>
            <div className="text-xs text-indigo-200/60">
              33 Mohawk Path, Bellingham
            </div>
          </div>
        </div>

        {/* Phase 2 */}
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-rose-400"></div>
          </div>
          <div>
            <span className="text-xs font-bold text-rose-300 uppercase">
              8:30 PM Move
            </span>
            <div className="text-sm font-medium text-white">
              Arigna Irish Pub
            </div>
            <div className="text-xs text-indigo-200/60 mb-2">
              799 S Main St, Bellingham
            </div>

            <div className="bg-rose-500/20 border border-rose-500/30 rounded px-2 py-1 text-xs text-rose-200 inline-block mb-3">
              ⚠️ Melyssa arrival approx 9:30 PM
            </div>

            {/* Collapsible Directions Toggle */}
            <button
              onClick={() => setShowDirections(!showDirections)}
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-indigo-300 hover:text-white transition-colors"
            >
              <Navigation className="w-3 h-3" />
              {showDirections
                ? "Hide Melyssa's Directions"
                : "View Melyssa's Directions"}
            </button>

            {/* Directions Content Pop-up */}
            {showDirections && (
              <div className="mt-3 bg-white/10 p-3 rounded-lg border border-white/20 animate-in slide-in-from-top-2 relative">
                <pre className="text-xs text-indigo-100 whitespace-pre-wrap font-sans leading-relaxed">
                  {directionsText}
                </pre>
                {/* Copy Button */}
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 p-1.5 bg-white/10 hover:bg-white/20 rounded-md transition-colors text-indigo-200 hover:text-white"
                  title="Copy to clipboard"
                >
                  {isCopied ? (
                    <Check className="w-3 h-3 text-emerald-400" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
